import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { ApprovalCard } from "@/components/ApprovalCard";
import { DetailSkeleton } from "@/components/Skeletons";
import { openClientPortalPreview } from "@/lib/clientPortalPreview";
import { EmailConfirmModal } from "@/components/EmailConfirmModal";
import { useEmailGuard } from "@/hooks/useEmailGuard";
import { isHardDeletable, splitStaffByActive, filterStaffUsers } from "./adminStaffHelpers";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, Power, Mail, CheckCircle2, XCircle, Wallet, Users as UsersIcon, ChevronDown, Sparkles, Trash2, Loader2, Search, Edit2, Ban, RotateCcw, UserX, X, ExternalLink, Handshake, FlaskConical, UserCheck2, ShieldOff, Youtube, UsersRound, ArrowRight, Eye, Upload, FileSpreadsheet } from "lucide-react";

// Must match backend VALID_ROLES in modules/admin.py exactly. freelancer/production_partner
// are deliberately excluded — those roles are only ever assigned automatically by the NJM
// Build Google login flow (access_resolver.py), never editable from this admin panel.
const ROLES = ["founder", "partner", "pm", "coordinator", "finance", "creative", "guest"];
// Individual staff logins (2026-11) — roles the "Add staff account" modal may ever create.
// Must match backend STAFF_MANAGED_ROLES (modules/admin.py) exactly. founder/partner are
// filtered out below unless the viewer is a founder (backend enforces the same rule either way).
const STAFF_CREATE_ROLES = ["founder", "partner", "pm", "coordinator", "finance", "creative"];
const SOURCE_LABELS = {
  demo: "Demo",
  google_workspace_staff: "Google Workspace",
  individual_staff_password: "Individual login",
  njm_build_freelancer: "NJM Build — Freelancer",
  njm_build_partner: "NJM Build — Partner",
};

function relativeTime(iso) {
  if (!iso) return "never";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// Email Audit outcome colors (2026-02, closing out the email-leak incident): `blocked_non_
// production` gets its OWN distinct amber "the safeguard just did its job" color — it's not a
// failure (red) and it's not a real delivery (green), it's the send-gate correctly protecting
// a non-production environment. `staff_preview_no_email` is a neutral "nothing was ever sent"
// gray, distinct from the default "no data" gray so a founder scanning the log can tell "no
// email attempted, by design" apart from "we genuinely don't know".
function outcomeBadgeCls(outcome) {
  if (outcome === "delivered" || outcome === "sent") return "bg-emerald-500/10 text-emerald-600";
  if (outcome === "bounced" || outcome === "complained" || outcome === "failed") return "bg-red-500/10 text-red-600";
  if (outcome === "blocked_non_production") return "bg-amber-500/10 text-amber-700";
  if (outcome === "staff_preview_no_email") return "bg-sky-500/10 text-sky-700";
  return "bg-[#F5F5F4] text-[#57534E]";
}

/** Small founder-facing chip for a connector's live status — no raw JSON, just Connected/Attention. */
function ConnectorChip({ label, connected, testId }) {
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-wider font-medium ${connected ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-600" : "border-red-500/30 bg-red-500/5 text-red-600"}`}>
      {connected ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} {label}: {connected ? "Connected" : "Needs attention"}
    </span>
  );
}

/** Two-connection Gmail support (2026-02) — ONE card per named connection ("info"/"finance"),
 * each with its OWN independent Connect/Reconnect button. The button is ALWAYS visible to a
 * founder regardless of current connected status (never hidden behind an "already connected"
 * state, since that state can be stale — this is the exact fix the founder asked for) — the
 * ONLY thing that changes is the label ("Connect" vs "Reconnect"). */
function GmailConnectionCard({ connection, title, mailboxLabel, status, configured, isFounder, onConnect, extraAction }) {
  const connected = !!status?.connected;
  return (
    <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid={`gmail-card-${connection}`}>
      <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
        <div className="text-xs uppercase tracking-wider text-[#A8A29E]">{title}</div>
        <div className="text-[10px] text-[#A8A29E]">{mailboxLabel}</div>
      </header>
      <div className="px-4 py-3 flex items-center justify-between gap-2">
        {connected ? (
          <div className="flex items-center gap-1.5 text-xs text-emerald-600" data-testid={`gmail-connected-${connection}`}><CheckCircle2 className="h-3.5 w-3.5" /> Connected</div>
        ) : (
          <div className="text-xs text-[#57534E]" data-testid={`gmail-not-connected-${connection}`}>{configured ? "Not connected" : "GMAIL_CLIENT_ID/SECRET not configured"}</div>
        )}
        <div className="flex items-center gap-2">
          {extraAction}
          {configured && (
            isFounder ? (
              <button data-testid={`connect-gmail-btn-${connection}`} onClick={onConnect} className="inline-flex items-center gap-1.5 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-3 py-1.5 text-xs text-[#B45309] hover:bg-[#FFC817]/20">
                <Mail className="h-3.5 w-3.5" /> {connected ? "Reconnect" : "Connect"}
              </button>
            ) : (
              <div data-testid={`connect-gmail-founder-only-${connection}`} className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Founder only</div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const isFounder = user?.role === "founder";
  const canManageUsers = ["founder", "partner"].includes(user?.role);
  const canApproveEmails = ["founder", "partner", "pm"].includes(user?.role);
  const [modules, setModules] = useState([]);
  const [health, setHealth] = useState(null);
  const [audit, setAudit] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const emailTestGuard = useEmailGuard();
  const approvalGuard = useEmailGuard();
  // Individual staff logins (2026-11) — shared by create-staff / resend-invite / send-password-
  // reset: all three can be deferred by the Email Send Guard, exactly like the agency's own
  // send-invite flow.
  const staffEmailGuard = useEmailGuard();
  const [emailApprovals, setEmailApprovals] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [approvalActionBusyId, setApprovalActionBusyId] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [zohoStatus, setZohoStatus] = useState(null);
  const [youtubeChannelStatus, setYoutubeChannelStatus] = useState(null);
  const [syncingYoutube, setSyncingYoutube] = useState(false);
  const [cleanupReport, setCleanupReport] = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [emailHealth, setEmailHealth] = useState(null);
  const [sendLog, setSendLog] = useState([]);
  const [sendLogTotal, setSendLogTotal] = useState(0);
  const [sendLogSummary, setSendLogSummary] = useState({});
  const [sendLogOutcomeFilter, setSendLogOutcomeFilter] = useState(null);
  const [sendLogTemplateFilter, setSendLogTemplateFilter] = useState("all");
  const [testSendTemplate, setTestSendTemplate] = useState("calendly_invite");
  const [testSendTo, setTestSendTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [users, setUsers] = useState([]);
  const [partnerRows, setPartnerRows] = useState([]);
  const [testAccountRows, setTestAccountRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purging, setPurging] = useState(false);
  const [previewingPurge, setPreviewingPurge] = useState(false);
  // Cursor review fix batch (2026-02) item 5 — "Purge test records" (distinct tool from
  // "Purge Test Data" above: this one targets rows that are normally Notion-backed but were
  // created with no Notion page at all, plus community posts/comments and users).
  const [purgeRecordsPreview, setPurgeRecordsPreview] = useState(null);
  const [previewingPurgeRecords, setPreviewingPurgeRecords] = useState(false);
  const [purgingRecords, setPurgingRecords] = useState(false);
  const [purgeRecordsTypedCount, setPurgeRecordsTypedCount] = useState("");
  // Minimal Freelancer Onboarding V1, Step 3 UI (2026-08): founder-only migration screen —
  // reuses the existing dry-run/commit/log endpoints as-is, no new backend.
  const [migrationFile, setMigrationFile] = useState(null);
  const [migrationDryRun, setMigrationDryRun] = useState(null);
  const [runningMigrationDryRun, setRunningMigrationDryRun] = useState(false);
  const [committingMigration, setCommittingMigration] = useState(false);
  const [migrationLog, setMigrationLog] = useState(null);
  const [loadingMigrationLog, setLoadingMigrationLog] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  // Deactivated Tab (2026-02) — replaces the old "All statuses/Active/Deactivated" dropdown
  // filter (now redundant with this tab split) as the single way to view deactivated staff.
  const [staffTab, setStaffTab] = useState("active");
  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("");
  const [savingUser, setSavingUser] = useState(false);
  const [userActionBusy, setUserActionBusy] = useState(null);
  const [demoPreview, setDemoPreview] = useState(null);
  const [previewingDemo, setPreviewingDemo] = useState(false);
  const [purgingDemo, setPurgingDemo] = useState(false);
  const [seedingDisabled, setSeedingDisabled] = useState(null);
  const [togglingSeeding, setTogglingSeeding] = useState(false);
  const [applyV2Enabled, setApplyV2Enabled] = useState(null);
  const [togglingApplyV2, setTogglingApplyV2] = useState(false);
  // Client Portal per-client pilot access (2026-02) — deny-by-default list of Client
  // companies + whether the portal login is flagged on for each (replaces the old global
  // client_portal_enabled toggle). Search-filterable since the real Clients list is 100+ rows.
  const [portalAccessList, setPortalAccessList] = useState([]);
  const [portalAccessSearch, setPortalAccessSearch] = useState("");
  const [togglingPortalAccessId, setTogglingPortalAccessId] = useState(null);
  const [previewingPortalAccessId, setPreviewingPortalAccessId] = useState(null);
  // Individual staff logins (2026-11) — founder/partner-only "Add staff account" flow.
  // Replaces the old admin-typed-temp-password PM-only flow: the account is created with NO
  // password at all and the person sets their own via an emailed invite link.
  const [showCreateStaff, setShowCreateStaff] = useState(false);
  const [newStaffName, setNewStaffName] = useState("");
  const [newStaffEmail, setNewStaffEmail] = useState("");
  const [newStaffRole, setNewStaffRole] = useState("pm");
  const [creatingStaff, setCreatingStaff] = useState(false);
  const [createdStaffResult, setCreatedStaffResult] = useState(null); // { email, link, outcome }
  const [staffActionBusyId, setStaffActionBusyId] = useState(null); // resend-invite / reset-password in flight
  const [staffActionResult, setStaffActionResult] = useState(null); // { userId, link, outcome }
  // Reassign Work Between Staff (2026-11) — founder-only, moves projects.pm_user_id and
  // tasks.assignee from a departing/shared account to an individual staff account.
  const [reassignUser, setReassignUser] = useState(null); // source row, or null when modal closed
  const [reassignTargetId, setReassignTargetId] = useState("");
  const [reassignIncludeProjects, setReassignIncludeProjects] = useState(true);
  const [reassignIncludeTasks, setReassignIncludeTasks] = useState(true);
  const [reassignScope, setReassignScope] = useState("open");
  const [reassignDryRun, setReassignDryRun] = useState(null);
  const [reassignApplyResult, setReassignApplyResult] = useState(null);
  const [reassignBusy, setReassignBusy] = useState(false);
  // Stuck Films/Deliverables Notion-sync recovery (Ticket 006) — list + one-at-a-time retry.
  const [stuckSync, setStuckSync] = useState({ items: [], total: 0, note: "", gave_up_after: 30 });
  const [retryingStuckId, setRetryingStuckId] = useState(null);
  const [stuckRetryResult, setStuckRetryResult] = useState(null);
  // Part 6 hardening (2026-10) — Sync Health's pending_create visibility: lazily fetched on
  // first "View" click (not on every page load — pending_create is usually 0), then cached
  // for the rest of this page visit; toggling the same entity again just hides the panel.
  const [pendingCreate, setPendingCreate] = useState(null);
  const [loadingPendingCreate, setLoadingPendingCreate] = useState(false);
  const [expandedPendingEntity, setExpandedPendingEntity] = useState(null);

  const refresh = async () => {
    const [reg, h, a, ap, ai, gm, zh, yt, us, eh, el, av2, pa, ss] = await Promise.all([
      api.get("/v1/admin/registry").catch(() => ({ data: { modules: [] } })),
      api.get("/v1/admin/sync/health").catch(() => ({ data: null })),
      api.get("/v1/admin/audit").catch(() => ({ data: { events: [] } })),
      api.get("/v1/admin/approvals").catch(() => ({ data: { items: [] } })),
      api.get("/v1/admin/ai/usage").catch(() => ({ data: null })),
      api.get("/v1/admin/gmail/status").catch(() => ({ data: { configured: false } })),
      api.get("/v1/admin/zoho/status").catch(() => ({ data: { configured: false } })),
      api.get("/v1/admin/youtube-channel/status").catch(() => ({ data: { configured: false } })),
      api.get("/v1/admin/users/sectioned").catch(() => ({ data: { staff: [], freelancers: [], partners: [], test_accounts: [] } })),
      api.get("/v1/build/admin/emails/health").catch(() => ({ data: { configured: false } })),
      api.get("/v1/build/admin/emails/log").catch(() => ({ data: { items: [] } })),
      api.get("/v1/build/apply/v2/status").catch(() => ({ data: { enabled: false } })),
      api.get("/v1/client-portal/admin/portal-access").catch(() => ({ data: { items: [] } })),
      api.get("/v1/admin/stuck-sync").catch(() => ({ data: { items: [], total: 0, note: "", gave_up_after: 30 } })),
    ]);
    setModules(reg.data.modules); setHealth(h.data); setAudit(a.data.events);
    setApprovals(ap.data.items); setAiUsage(ai.data); setGmailStatus(gm.data); setZohoStatus(zh.data); setYoutubeChannelStatus(yt.data);
    setUsers(us.data.staff); setPartnerRows(us.data.partners); setTestAccountRows(us.data.test_accounts);
    setEmailHealth(eh.data); setSendLog(el.data.items); setSendLogTotal(el.data.total || el.data.items.length); setSendLogSummary(el.data.summary || {});
    setApplyV2Enabled(!!av2.data.enabled);
    setPortalAccessList(pa.data.items);
    setStuckSync({
      items: ss.data.items || [],
      total: ss.data.total || 0,
      note: ss.data.note || "",
      gave_up_after: ss.data.gave_up_after || 30,
    });
  };

  const refreshStuckSync = () => api.get("/v1/admin/stuck-sync").then((r) => {
    setStuckSync({
      items: r.data.items || [],
      total: r.data.total || 0,
      note: r.data.note || "",
      gave_up_after: r.data.gave_up_after || 30,
    });
  }).catch(() => {});

  const PENDING_CREATE_ENTITY_ROUTE = { clients: "/clients", contacts: "/contacts", projects: "/projects" };

  const togglePendingCreate = async (entity) => {
    if (expandedPendingEntity === entity) { setExpandedPendingEntity(null); return; }
    setExpandedPendingEntity(entity);
    if (pendingCreate) return;
    setLoadingPendingCreate(true);
    try {
      const r = await api.get("/v1/admin/sync/pending-create");
      setPendingCreate(r.data.items || {});
    } catch {
      toast.error("Couldn't load pending-create records");
    } finally {
      setLoadingPendingCreate(false);
    }
  };

  const retryStuckSync = async (row) => {
    const label = row.deliverable_name || row.id;
    if (!window.confirm(`Retry Notion sync for “${label}”? This writes to Notion once and reports the real result. It will not recreate a deleted Notion page.`)) return;
    setRetryingStuckId(row.id);
    try {
      const r = await api.post(`/v1/admin/stuck-sync/${row.id}/retry`, { confirm: true });
      setStuckRetryResult({ id: row.id, ...r.data });
      if (r.data.ok) {
        toast.success(`${label} synced to Notion`);
      } else {
        toast.error(`${label} still failing: ${r.data.error_reason || r.data.error || "unknown"}`);
      }
      await refreshStuckSync();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : detail;
      toast.error(code === "permission_denied" ? "Founder only" : "Couldn't retry this record");
      setStuckRetryResult({ id: row.id, ok: false, error: code || "request_failed" });
    } finally {
      setRetryingStuckId(null);
    }
  };
  const refreshEmailApprovals = useCallback(async () => {
    if (!canApproveEmails) return;
    setLoadingApprovals(true);
    try {
      const r = await api.get("/v1/collaboration/email-approvals");
      setEmailApprovals(r.data.items || []);
    } catch (e) {
      // non-fatal — just leaves the list empty/stale, same as every other Admin widget here
    } finally {
      setLoadingApprovals(false);
    }
  }, [canApproveEmails]);
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);
  useEffect(() => { refreshEmailApprovals(); }, [refreshEmailApprovals]);

  const toggleApplyV2 = async () => {
    setTogglingApplyV2(true);
    try {
      const r = await api.post("/v1/build/apply/v2/toggle", { enabled: !applyV2Enabled });
      setApplyV2Enabled(r.data.enabled);
      toast.success(r.data.enabled ? "Application Form v2 is now live at /build/apply/v2" : "Application Form v2 turned off — v1 stays live");
    } catch {
      toast.error("Couldn't update the toggle — try again.");
    } finally {
      setTogglingApplyV2(false);
    }
  };

  const refreshSendLog = () => api.get("/v1/build/admin/emails/log").then((r) => { setSendLog(r.data.items); setSendLogTotal(r.data.total || r.data.items.length); setSendLogSummary(r.data.summary || {}); }).catch(() => {});

  // Client Portal pilot access (2026-02) — optimistic toggle, reverted on failure.
  const togglePortalAccess = async (row) => {
    setTogglingPortalAccessId(row.company_remote_id);
    const nextEnabled = !row.enabled;
    try {
      await api.post(`/v1/client-portal/admin/portal-access/${row.company_remote_id}`, { enabled: nextEnabled });
      setPortalAccessList((prev) => prev.map((r) => (r.company_remote_id === row.company_remote_id ? { ...r, enabled: nextEnabled } : r)));
      toast.success(`${row.company_name} ${nextEnabled ? "can now sign in to" : "is blocked from"} the Client Portal`);
    } catch {
      toast.error("Couldn't update Client Portal access — try again.");
    } finally {
      setTogglingPortalAccessId(null);
    }
  };
  const filteredPortalAccess = portalAccessList.filter((r) => (r.company_name || "").toLowerCase().includes(portalAccessSearch.toLowerCase()));
  const enabledPortalAccessCount = portalAccessList.filter((r) => r.enabled).length;

  // "Preview as client" (2026-02; now shared with Clients.jsx via openClientPortalPreview,
  // read-only/attributable/session-safe as of the 2026-09 PM Client Portal Preview fix — see
  // /app/memory/NJM_OS_PM_CLIENT_PORTAL_PREVIEW_AUDIT.md).
  const previewAsClient = async (row) => {
    setPreviewingPortalAccessId(row.company_remote_id);
    await openClientPortalPreview(row.company_remote_id, row.company_name);
    setPreviewingPortalAccessId(null);
  };

  const approveEmail = async (approvalId) => {
    setApprovalActionBusyId(approvalId);
    try {
      const r = await api.post(`/v1/collaboration/email-approvals/${approvalId}/approve`, {});
      // H2.3 — approving goes through the SAME two-step preview/confirm as any other guarded
      // send; nothing is dispatched until the founder/partner/pm confirms THIS modal too.
      if (approvalGuard.intercept(r.data, async (typedCount) => {
        await api.post(`/v1/collaboration/email-approvals/${approvalId}/approve`, { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
        toast.success("Approved — email sending");
        refreshEmailApprovals();
      })) return;
      toast.success("Approved — email sending");
      refreshEmailApprovals();
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      if (detail.code === "cannot_approve_own_post") toast.error("You can't approve your own post");
      else toast.error("Couldn't approve this request");
    } finally {
      setApprovalActionBusyId(null);
    }
  };

  const rejectEmail = async (approvalId) => {
    setApprovalActionBusyId(approvalId);
    try {
      await api.post(`/v1/collaboration/email-approvals/${approvalId}/reject`);
      toast.success("Rejected — nothing will be sent");
      refreshEmailApprovals();
    } catch (e) {
      toast.error("Couldn't reject this request");
    } finally {
      setApprovalActionBusyId(null);
    }
  };

  const sendTestEmail = async () => {
    if (!testSendTo.trim()) { toast.error("Enter a recipient address"); return; }
    setSendingTest(true);
    try {
      const r = await api.post("/v1/build/admin/emails/test-send", { to: testSendTo.trim(), template: testSendTemplate });
      // Email Send Guard (2026-02): this QA tool can address any real external person —
      // nothing is sent at all until the shared "This will email N people" modal is
      // confirmed with a server-issued token.
      if (emailTestGuard.intercept(r.data, async (typedCount) => {
        const cr = await api.post("/v1/build/admin/emails/test-send", { to: testSendTo.trim(), template: testSendTemplate, confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
        if (cr.data.blocked) toast.warning(`Blocked — ${cr.data.reason || cr.data.error || "not sent"}`);
        else toast.success(`Sent — check ${testSendTo.trim()}`);
        refreshSendLog();
      })) return;
      if (r.data.blocked) {
        toast.warning(`Blocked — ${r.data.reason || r.data.error || "not sent"}`);
      } else {
        toast.success(`Sent — check ${testSendTo.trim()}`);
      }
      refreshSendLog();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.error || detail?.code || "Couldn't send");
    } finally {
      setSendingTest(false);
    }
  };

  // Users-only refetch — used after any Users-section mutation so the row updates immediately,
  // without waiting on the OTHER 7 admin sections' calls in refresh() above (some of which, e.g.
  // sync/health, hit live Notion and can take several seconds — awaiting the full Promise.all
  // there was silently delaying the Users list update by the same amount).
  const refreshUsers = async () => {
    const r = await api.get("/v1/admin/users/sectioned").catch(() => ({ data: { staff: [], freelancers: [], partners: [], test_accounts: [] } }));
    setUsers(r.data.staff); setPartnerRows(r.data.partners); setTestAccountRows(r.data.test_accounts);
  };

  // Deactivated Tab (2026-02): default view is every active + pending-invite account; a
  // separate "Deactivated" tab holds every deactivated account (demo and individual-login
  // rows included). Both are derived from the same `users` state (adminStaffHelpers.js), so
  // deactivating/reactivating a row (which already calls refreshUsers()) moves it between tabs
  // on the next render with no page reload and no extra wiring here.
  const { active: activeStaffUsers, deactivated: deactivatedStaffUsers } = splitStaffByActive(users);
  const staffTabUsers = staffTab === "deactivated" ? deactivatedStaffUsers : activeStaffUsers;
  const filteredUsers = filterStaffUsers(staffTabUsers, { search: userSearch, role: roleFilter, source: sourceFilter });

  const openEdit = (u) => { setEditingUser(u); setEditName(u.name || ""); setEditRole(u.role); };
  const closeEdit = () => setEditingUser(null);

  const saveEdit = async () => {
    if (!editingUser) return;
    setSavingUser(true);
    try {
      const body = { name: editName };
      if (ROLES.includes(editingUser.role) && editingUser.id !== user.id) body.role = editRole;
      await api.patch(`/v1/admin/users/${editingUser.id}`, body);
      toast.success("User updated");
      setEditingUser(null);
      refreshUsers();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        detail?.code === "last_founder_protected" ? (detail.message || "This must remain the organisation's last active founder.")
        : detail?.code === "cannot_change_own_role" ? detail.message
        : detail?.code === "founder_only_role" ? detail.message
        : "Couldn't update user"
      );
    } finally {
      setSavingUser(false);
    }
  };

  const deactivateUser = async (u) => {
    if (!window.confirm(`Deactivate ${u.name}? Their access is revoked instantly.`)) return;
    setUserActionBusy(u.id);
    try {
      await api.post(`/v1/admin/users/${u.id}/deactivate`);
      toast.success(`${u.name} deactivated`);
      refreshUsers();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.code === "last_founder_protected" ? (detail.message || "Cannot deactivate the last active founder.") : "Couldn't deactivate user");
    } finally {
      setUserActionBusy(null);
    }
  };

  const reactivateUser = async (u) => {
    setUserActionBusy(u.id);
    try {
      await api.post(`/v1/admin/users/${u.id}/reactivate`);
      toast.success(`${u.name} reactivated`);
      refreshUsers();
    } catch {
      toast.error("Couldn't reactivate user");
    } finally {
      setUserActionBusy(null);
    }
  };

  const hardDeleteUser = async (u) => {
    if (!window.confirm(`Permanently delete demo account ${u.email}? This cannot be undone.`)) return;
    setUserActionBusy(u.id);
    try {
      await api.delete(`/v1/admin/users/${u.id}`);
      toast.success(`${u.email} removed`);
      refreshUsers();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Show the backend's own message/code instead of a generic failure string — the backend
      // already enforces exactly which accounts this can hard-delete (hard_remove_restricted_
      // to_seed_accounts, last_founder_protected, user_not_found, …); surface whichever one fired.
      toast.error(detail?.message || detail?.code || "Couldn't remove user");
    } finally {
      setUserActionBusy(null);
    }
  };

  const createStaffAccount = async () => {
    if (!newStaffName.trim() || !newStaffEmail.trim()) { toast.error("Name and email are required"); return; }
    setCreatingStaff(true);
    try {
      const r = await api.post("/v1/admin/users/create-staff", { name: newStaffName.trim(), email: newStaffEmail.trim().toLowerCase(), role: newStaffRole });
      const intercepted = staffEmailGuard.intercept(r.data, async (typedCount) => {
        const cr = await api.post("/v1/admin/users/create-staff/confirm", { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
        setCreatedStaffResult({ email: r.data.user.email, ...cr.data.email_result });
      });
      if (!intercepted) setCreatedStaffResult({ email: r.data.user.email, ...r.data.email_result });
      setNewStaffName(""); setNewStaffEmail(""); setNewStaffRole("pm");
      refreshUsers();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        detail?.code === "email_already_exists" ? "That email already has an account"
        : detail?.code === "founder_only_role" ? detail.message
        : "Couldn't create the staff account"
      );
    } finally {
      setCreatingStaff(false);
    }
  };

  const closeCreateStaff = () => { setShowCreateStaff(false); setCreatedStaffResult(null); setNewStaffName(""); setNewStaffEmail(""); setNewStaffRole("pm"); };

  const resendInvite = async (u) => {
    setStaffActionBusyId(u.id);
    try {
      const r = await api.post(`/v1/admin/users/${u.id}/resend-invite`);
      const intercepted = staffEmailGuard.intercept(r.data, async (typedCount) => {
        const cr = await api.post(`/v1/admin/users/${u.id}/resend-invite/confirm`, { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
        setStaffActionResult({ userId: u.id, ...cr.data.email_result });
        toast.success(`Invite resent to ${u.email}`);
      });
      if (!intercepted) { setStaffActionResult({ userId: u.id, ...r.data.email_result }); toast.success(`Invite resent to ${u.email}`); }
    } catch {
      toast.error("Couldn't resend the invite");
    } finally {
      setStaffActionBusyId(null);
    }
  };

  const sendPasswordReset = async (u) => {
    setStaffActionBusyId(u.id);
    try {
      const r = await api.post(`/v1/admin/users/${u.id}/send-password-reset`);
      const intercepted = staffEmailGuard.intercept(r.data, async (typedCount) => {
        const cr = await api.post(`/v1/admin/users/${u.id}/send-password-reset/confirm`, { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
        setStaffActionResult({ userId: u.id, ...cr.data.email_result });
        toast.success(`Password reset link sent to ${u.email}`);
      });
      if (!intercepted) { setStaffActionResult({ userId: u.id, ...r.data.email_result }); toast.success(`Password reset link sent to ${u.email}`); }
    } catch {
      toast.error("Couldn't send the password reset link");
    } finally {
      setStaffActionBusyId(null);
    }
  };

  const openReassign = (u) => {
    setReassignUser(u);
    setReassignTargetId("");
    setReassignIncludeProjects(true);
    setReassignIncludeTasks(true);
    setReassignScope("open");
    setReassignDryRun(null);
    setReassignApplyResult(null);
  };
  const closeReassign = () => setReassignUser(null);

  const runReassignDryRun = async () => {
    if (!reassignTargetId) { toast.error("Pick a target account first"); return; }
    setReassignBusy(true);
    setReassignDryRun(null);
    try {
      const r = await api.post("/v1/admin/users/reassign-work/dry-run", {
        source_user_id: reassignUser.id,
        target_user_id: reassignTargetId,
        include_projects: reassignIncludeProjects,
        include_tasks: reassignIncludeTasks,
        scope: reassignScope,
      });
      setReassignDryRun(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || detail?.code || "Couldn't check what would move");
    } finally {
      setReassignBusy(false);
    }
  };

  const applyReassign = async () => {
    if (!reassignDryRun) return;
    setReassignBusy(true);
    try {
      const r = await api.post("/v1/admin/users/reassign-work/apply", {
        source_user_id: reassignUser.id,
        target_user_id: reassignTargetId,
        include_projects: reassignIncludeProjects,
        include_tasks: reassignIncludeTasks,
        scope: reassignScope,
        confirm: true,
        project_ids: reassignDryRun.projects.map((p) => p.id),
        task_ids: reassignDryRun.tasks.map((t) => t.id),
        expected_project_count: reassignDryRun.counts.projects,
        expected_task_count: reassignDryRun.counts.tasks,
      });
      setReassignApplyResult(r.data);
      toast.success(`Moved ${r.data.counts.projects} project(s) and ${r.data.counts.tasks} task(s)`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || detail?.code || "Couldn't apply — run the dry run again");
      setReassignDryRun(null);
    } finally {
      setReassignBusy(false);
    }
  };



  const previewDemoPurge = async () => {
    setPreviewingDemo(true);
    try {
      const r = await api.get("/v1/admin/users/seed-accounts/preview");
      setDemoPreview(r.data);
      if (r.data.total === 0) toast.success("No demo/seed accounts found");
    } catch {
      toast.error("Couldn't scan seed accounts");
    } finally {
      setPreviewingDemo(false);
    }
  };

  const runDemoPurge = async () => {
    if (!demoPreview || demoPreview.total === 0) return;
    if (!window.confirm(`Permanently delete ${demoPreview.total} demo/seed account(s)? This cannot be undone.`)) return;
    setPurgingDemo(true);
    try {
      const r = await api.post("/v1/admin/users/seed-accounts/purge", { confirm: true });
      toast.success(`Removed ${r.data.total_removed} account(s)${r.data.skipped.length ? `, ${r.data.skipped.length} skipped (last-founder guardrail)` : ""} — re-seeding is now permanently disabled`);
      setDemoPreview(null);
      setSeedingDisabled(true);
      refreshUsers();
    } catch {
      toast.error("Couldn't purge demo accounts");
    } finally {
      setPurgingDemo(false);
    }
  };

  useEffect(() => {
    if (isFounder) api.get("/v1/admin/users/seeding-status").then((r) => setSeedingDisabled(r.data.seeding_disabled)).catch(() => {});
  }, [isFounder]);

  const toggleSeeding = async () => {
    setTogglingSeeding(true);
    try {
      const r = await api.post("/v1/admin/users/seeding-status", { seeding_disabled: !seedingDisabled });
      setSeedingDisabled(r.data.seeding_disabled);
      toast.success(r.data.seeding_disabled ? "Demo-account re-seeding disabled" : "Demo-account re-seeding enabled");
    } catch {
      toast.error("Couldn't update seeding status");
    } finally {
      setTogglingSeeding(false);
    }
  };

  const connectGmail = async (connection) => {
    try {
      const r = await api.get(`/v1/admin/gmail/authorise/${connection}`);
      window.location.href = r.data.url;
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Only the founder role can connect Gmail");
      else toast.error("Couldn't start Gmail connection");
    }
  };

  const refreshEmailActivity = async () => {
    setBusy(true);
    try {
      const r = await api.post("/v1/email/activity/refresh");
      toast.success(`Email activity refreshed — ${r.data.count} contact(s) mirrored`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.reason === "gmail_not_connected" ? "Connect Gmail first" : "Couldn't refresh email activity");
    } finally { setBusy(false); }
  };

  const connectZoho = async () => {
    try {
      const r = await api.get("/v1/admin/zoho/authorise");
      window.location.href = r.data.url;
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Only the founder role can connect Zoho Books");
      else toast.error("Couldn't start Zoho connection");
    }
  };

  const connectYoutubeChannel = async () => {
    try {
      const r = await api.get("/v1/admin/youtube-channel/authorise");
      window.location.href = r.data.url;
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Only the founder role can connect the YouTube channel");
      else toast.error("Couldn't start YouTube channel connection");
    }
  };

  const syncYoutubeChannelNow = async () => {
    setSyncingYoutube(true);
    try {
      const r = await api.post("/v1/videos/sync/youtube-channel");
      toast.success(`Checked ${r.data.checked} upload(s) — ${r.data.created_count} new video(s) added (Needs Review)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code === "youtube_channel_not_connected" ? "Connect the YouTube channel first" : "Couldn't sync the YouTube channel");
    } finally { setSyncingYoutube(false); }
  };

  const runLibraryCleanupDryRun = async () => {
    setCleanupRunning(true);
    try {
      const r = await api.get("/v1/videos/library-cleanup/dry-run");
      setCleanupReport(r.data);
      toast.success("Dry-run report ready — nothing archived or deleted");
    } catch (e) {
      toast.error("Couldn't generate the cleanup report");
    } finally { setCleanupRunning(false); }
  };

  const toggle = async (key, enabled) => {
    await api.post(`/v1/admin/registry/${key}/toggle`, { enabled });
    toast.success(`${key} ${enabled ? "enabled" : "disabled"}`);
    refresh();
  };

  const runSync = async (type) => {
    setBusy(true);
    try {
      await api.post(`/v1/admin/sync/${type}`);
      toast.success(`${type} completed`);
      refresh();
    } finally { setBusy(false); }
  };

  const simulateConflict = async () => {
    // pick first client
    const clients = await api.get("/v1/clients/");
    const first = clients.data.items[0];
    if (!first) return;
    await api.post("/v1/admin/sync/simulate-conflict", {
      entity: "clients", notion_id: first.sync?.notion_id, patch: { brief: "External edit from Notion at " + new Date().toISOString() },
    });
    // Now propose a write on same client → conflict
    await api.post(`/v1/projects/${first.id}/propose`, { name: "should-not-apply" }).catch(() => {});
    toast.message("Simulated external Notion edit — approve a pending proposal to see conflict handling.");
    refresh();
  };

  const reloadAi = async () => {
    const r = await api.post("/v1/admin/ai/reload");
    toast.success(`AI config reloaded — default: ${r.data.config.default_provider}`);
    refresh();
  };

  // QA fixture cleanup (2026-02): founder-only tool clearing Mongo-native TEST_/ZZZTEST_
  // debris (draft quotes, generated SOWs, briefs, tasks) the test suite and manual QA leave
  // behind. Always previews counts first — the actual delete requires an explicit confirm.
  const previewPurge = async () => {
    setPreviewingPurge(true);
    try {
      const r = await api.get("/v1/admin/purge-test-data/preview");
      setPurgePreview(r.data);
      if (r.data.total === 0) toast.success("No TEST_/ZZZTEST_ fixtures found — already clean");
    } catch {
      toast.error("Couldn't scan for test fixtures");
    } finally {
      setPreviewingPurge(false);
    }
  };

  const runPurge = async () => {
    if (!purgePreview || purgePreview.total === 0) return;
    if (!window.confirm(`Permanently delete ${purgePreview.total} TEST_/ZZZTEST_ record(s) from Mongo? This cannot be undone.`)) return;
    setPurging(true);
    try {
      const r = await api.post("/v1/admin/purge-test-data", { confirm: true });
      toast.success(`Purged ${r.data.total} test fixture(s)`);
      setPurgePreview(null);
    } catch {
      toast.error("Couldn't purge test fixtures");
    } finally {
      setPurging(false);
    }
  };

  // "Purge test records" (2026-02): DRY-RUN FIRST by default — preview lists eligible rows
  // (test-named, no linked Notion page, not referenced by anything real) AND skipped rows
  // with a reason, per collection, across freelancers/clients/projects/build_opportunities/
  // assignments/collaboration_applications/community_posts/community_comments/users. Apply
  // requires typing the EXACT eligible count shown — re-verified server-side against a fresh
  // scan, not the stale number on screen.
  const previewPurgeRecords = async () => {
    setPreviewingPurgeRecords(true);
    try {
      const r = await api.get("/v1/admin/purge-test-records/preview");
      setPurgeRecordsPreview(r.data);
      setPurgeRecordsTypedCount("");
      if (r.data.total_eligible === 0) toast.success("No orphaned test records found — already clean");
    } catch {
      toast.error("Couldn't scan for test records");
    } finally {
      setPreviewingPurgeRecords(false);
    }
  };

  const runPurgeRecords = async () => {
    if (!purgeRecordsPreview || purgeRecordsPreview.total_eligible === 0) return;
    const typed = parseInt(purgeRecordsTypedCount, 10);
    if (typed !== purgeRecordsPreview.total_eligible) {
      toast.error(`Type ${purgeRecordsPreview.total_eligible} exactly to confirm`);
      return;
    }
    setPurgingRecords(true);
    try {
      const r = await api.post("/v1/admin/purge-test-records/apply", { confirmed_count: typed });
      toast.success(`Purged ${r.data.total} test record(s)`);
      setPurgeRecordsPreview(null);
      setPurgeRecordsTypedCount("");
    } catch (e) {
      if (e?.response?.data?.detail?.code === "count_mismatch") {
        toast.error(`The count changed — expected ${e.response.data.detail.expected}. Preview again.`);
      } else {
        toast.error("Couldn't purge test records");
      }
    } finally {
      setPurgingRecords(false);
    }
  };

  // Minimal Freelancer Onboarding V1, Step 3 UI (2026-08): same CSV file object is reused for
  // both the dry-run preview and the commit — the founder only ever uploads it once per run.
  const runMigrationDryRun = async () => {
    if (!migrationFile) return;
    setRunningMigrationDryRun(true);
    try {
      const form = new FormData();
      form.append("file", migrationFile);
      const r = await api.post("/v1/build/onboarding/migration/dry-run", form, { headers: { "Content-Type": "multipart/form-data" } });
      setMigrationDryRun(r.data);
    } catch {
      toast.error("Couldn't run the migration dry-run");
    } finally {
      setRunningMigrationDryRun(false);
    }
  };

  const commitMigration = async () => {
    if (!migrationFile || !migrationDryRun) return;
    if (!window.confirm(`Commit ${migrationDryRun.summary.matched} matched onboarding record(s)? Ambiguous/unmatched rows are never written.`)) return;
    setCommittingMigration(true);
    try {
      const form = new FormData();
      form.append("file", migrationFile);
      const r = await api.post("/v1/build/onboarding/migration/commit", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Migrated ${r.data.migrated_count}, skipped ${r.data.skipped_existing_count} (already had a record)`);
      setMigrationDryRun(null);
      setMigrationFile(null);
      loadMigrationLog();
    } catch {
      toast.error("Couldn't commit the migration");
    } finally {
      setCommittingMigration(false);
    }
  };

  const loadMigrationLog = async () => {
    setLoadingMigrationLog(true);
    try {
      const r = await api.get("/v1/build/onboarding/migration/log");
      setMigrationLog(r.data.items || []);
    } catch {
      toast.error("Couldn't load the migration log");
    } finally {
      setLoadingMigrationLog(false);
    }
  };

  if (loading) return <DetailSkeleton testId="admin-skeleton" />;

  const dbs = health?.engine?.knowledge_store?.dbs || {};
  const dbEntries = Object.entries(dbs);
  const unhealthyCount = dbEntries.filter(([, v]) => !v.ok).length;
  const notionConnected = dbEntries.length > 0 && unhealthyCount === 0;
  const readModel = health?.read_model || {};

  return (
    <div className="p-8 space-y-8" data-testid="admin-page">
      <h1 className="font-editorial text-4xl tracking-tight">Administration</h1>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="admin-staff-section">
        <header className="flex flex-col gap-3 border-b border-[#E7E5E4] px-4 py-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-wider text-[#A8A29E] flex items-center gap-1.5"><UsersIcon className="h-3.5 w-3.5" /> NJM Staff ({filteredUsers.length}/{users.length}) — new Google logins default to Guest until promoted</div>
            {canManageUsers && (
              <button data-testid="open-create-staff-modal-btn" onClick={() => setShowCreateStaff(true)} className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:bg-[#FFC817]/90">
                <UserCheck2 className="h-3.5 w-3.5" /> Add staff account
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              data-testid="staff-tab-active"
              onClick={() => setStaffTab("active")}
              className={`rounded-md px-3 py-1.5 text-xs font-medium ${staffTab === "active" ? "bg-[#1C1C1E] text-white" : "text-[#57534E] hover:bg-[#F5F5F4]"}`}
            >
              Active ({activeStaffUsers.length})
            </button>
            <button
              data-testid="staff-tab-deactivated"
              onClick={() => setStaffTab("deactivated")}
              className={`rounded-md px-3 py-1.5 text-xs font-medium ${staffTab === "deactivated" ? "bg-[#1C1C1E] text-white" : "text-[#57534E] hover:bg-[#F5F5F4]"}`}
            >
              Deactivated ({deactivatedStaffUsers.length})
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#A8A29E]" />
              <input
                data-testid="users-search-input"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search name or email…"
                className="w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] pl-8 pr-2.5 py-1.5 text-xs text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
              />
            </div>
            <select data-testid="users-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-xs text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50">
              <option value="">All roles</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select data-testid="users-source-filter" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-xs text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50">
              <option value="">All sources</option>
              {Object.entries(SOURCE_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
        </header>
        <div className="divide-y divide-[#E7E5E4]">
          {filteredUsers.length === 0 && <div className="px-4 py-6 text-center text-xs text-[#A8A29E]">No users match these filters</div>}
          {filteredUsers.map((u) => {
            const isActive = u.active !== false;
            // Deactivated Tab (2026-02) — matches modules/admin.py::remove_user's EXACT
            // condition (email in SEED_ACCOUNT_EMAILS), not the broader `source === "demo"`
            // check: a ZZZTest fixture row (auth_provider="demo", random email) or a real
            // njmpro.com/gmail account can ALSO show source="demo"-adjacent labels without
            // being in the hardcoded seed list, and the backend would 400 those — this keeps
            // the button from ever disagreeing with the backend's enforcement.
            const canHardDelete = isHardDeletable(u.email);
            const isBusy = userActionBusy === u.id;
            const isSelf = u.id === user.id;
            const isStaffActionBusy = staffActionBusyId === u.id;
            const lastStaffResult = staffActionResult?.userId === u.id ? staffActionResult : null;
            // H1 fix pass (2026-11): a partner may manage pm/coordinator/finance/creative/
            // guest rows, but must never see Edit/Deactivate/Reactivate/Resend/Reset on a
            // founder or another partner's row — only a founder can act on those (backend
            // enforces this too; this just keeps the UI from offering an action that would
            //403). A partner's OWN row is exempt.
            const canActOnThisUser = canManageUsers && (user.role === "founder" || isSelf || !["founder", "partner"].includes(u.role));
            return (
              <div key={u.id} data-testid={`user-row-${u.id}`} className={`flex items-center justify-between gap-3 px-4 py-3 ${!isActive ? "bg-red-500/5" : ""}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm text-[#1C1C1E]">
                    {u.name}
                    <span className="text-[10px] text-[#A8A29E] font-mono">{u.email}</span>
                    {!isActive && <span data-testid={`user-deactivated-badge-${u.id}`} className="rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-red-600">Deactivated</span>}
                    {isSelf && <span className="rounded-full bg-[#FFC817]/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#B45309]">You</span>}
                  </div>
                  <div className="text-[10px] text-[#A8A29E] mt-0.5 uppercase tracking-wider flex items-center gap-2">
                    <span>{SOURCE_LABELS[u.source] || u.source}</span>
                    <span>· {u.role}</span>
                    <span>· last login {relativeTime(u.last_login_at)}</span>
                    {u.source === "individual_staff_password" && !u.has_password && <span data-testid={`user-invite-pending-badge-${u.id}`} className="text-amber-600">· invite pending</span>}
                  </div>
                  {lastStaffResult && (
                    <div data-testid={`user-staff-action-result-${u.id}`} className="text-[10px] text-[#78716C] mt-1">
                      {lastStaffResult.outcome === "sent" ? "Link emailed." : lastStaffResult.outcome === "deferred_pending_confirmation" ? "Awaiting confirmation…" : "Email wasn't sent in this environment — nothing more to do here; this is never shown as a link (see security note)."}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {canActOnThisUser && (
                    <button data-testid={`user-edit-btn-${u.id}`} onClick={() => openEdit(u)} className="inline-flex items-center gap-1 rounded-md border border-[#E7E5E4] px-2.5 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309]">
                      <Edit2 className="h-3 w-3" /> Edit
                    </button>
                  )}
                  {canActOnThisUser && u.source === "individual_staff_password" && (u.has_password ? (
                    <button data-testid={`user-reset-password-btn-${u.id}`} disabled={isStaffActionBusy} onClick={() => sendPasswordReset(u)} className="inline-flex items-center gap-1 rounded-md border border-[#E7E5E4] px-2.5 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
                      <RotateCcw className="h-3 w-3" /> Reset password
                    </button>
                  ) : (
                    <button data-testid={`user-resend-invite-btn-${u.id}`} disabled={isStaffActionBusy} onClick={() => resendInvite(u)} className="inline-flex items-center gap-1 rounded-md border border-[#E7E5E4] px-2.5 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
                      <RotateCcw className="h-3 w-3" /> Resend invite
                    </button>
                  ))}
                  {canActOnThisUser && !isSelf && (isActive ? (
                    <button data-testid={`user-deactivate-btn-${u.id}`} disabled={isBusy} onClick={() => deactivateUser(u)} className="inline-flex items-center gap-1 rounded-md border border-orange-500/30 bg-orange-500/5 px-2.5 py-1.5 text-xs text-orange-600 hover:bg-orange-500/10 disabled:opacity-50">
                      <Ban className="h-3 w-3" /> Deactivate
                    </button>
                  ) : (
                    <button data-testid={`user-reactivate-btn-${u.id}`} disabled={isBusy} onClick={() => reactivateUser(u)} className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2.5 py-1.5 text-xs text-emerald-600 hover:bg-emerald-500/10 disabled:opacity-50">
                      <RotateCcw className="h-3 w-3" /> Reactivate
                    </button>
                  ))}
                  {canManageUsers && canHardDelete && (
                    <button data-testid={`user-hard-delete-btn-${u.id}`} disabled={isBusy} onClick={() => hardDeleteUser(u)} className="inline-flex items-center gap-1 rounded-md border border-red-500/30 bg-red-500/5 px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-500/10 disabled:opacity-50">
                      <UserX className="h-3 w-3" /> Remove
                    </button>
                  )}
                  {/* Reassign Work Between Staff (2026-11) — founder-only, regardless of the H1
                      canActOnThisUser rule above: a founder retiring info@/finance@ must be able
                      to move THAT account's work even though it's a shared/non-staff-managed row. */}
                  {isFounder && (
                    <button data-testid={`user-reassign-work-btn-${u.id}`} onClick={() => openReassign(u)} className="inline-flex items-center gap-1 rounded-md border border-[#E7E5E4] px-2.5 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309]">
                      <ArrowRight className="h-3 w-3" /> Reassign work…
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {isFounder && (
          <div className="border-t border-[#E7E5E4] px-4 py-3 space-y-2" data-testid="purge-demo-accounts-section">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Purge Demo Accounts — removes every @njm.local seed account</div>
              <button data-testid="purge-demo-preview-btn" disabled={previewingDemo} onClick={previewDemoPurge} className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
                {previewingDemo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Preview
              </button>
            </div>
            {seedingDisabled !== null && (
              <div className="flex items-center justify-between rounded-md bg-[#F5F5F4] px-3 py-2" data-testid="seeding-status-row">
                <div className="text-xs text-[#57534E]">
                  Re-seeding on restart: <span className={`font-semibold ${seedingDisabled ? "text-emerald-600" : "text-orange-600"}`} data-testid="seeding-status-value">
                    {seedingDisabled ? "Disabled — demo accounts will stay gone" : "Enabled — demo accounts will reappear on next restart/redeploy"}
                  </span>
                </div>
                <button data-testid="seeding-status-toggle-btn" disabled={togglingSeeding} onClick={toggleSeeding} className="rounded-md border border-[#E7E5E4] px-2.5 py-1 text-xs text-[#57534E] hover:border-[#FFC817] disabled:opacity-50">
                  {seedingDisabled ? "Re-enable" : "Disable now"}
                </button>
              </div>
            )}
            {demoPreview && (
              <div className="space-y-2" data-testid="purge-demo-preview-results">
                <div className="text-sm text-[#1C1C1E]">Found <span className="font-semibold" data-testid="purge-demo-total-count">{demoPreview.total}</span> demo/seed account(s)</div>
                {demoPreview.total > 0 && (
                  <>
                    <div className="flex flex-wrap gap-1.5">
                      {demoPreview.accounts.map((a) => <span key={a.email} className="rounded-full bg-[#F5F5F4] px-2 py-0.5 text-[10px] font-mono text-[#57534E]">{a.email}</span>)}
                    </div>
                    <button data-testid="purge-demo-confirm-btn" disabled={purgingDemo} onClick={runDemoPurge} className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">
                      {purgingDemo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserX className="h-3.5 w-3.5" />} {purgingDemo ? "Purging…" : `Permanently remove ${demoPreview.total} account(s)`}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {isFounder && (
        <section className="rounded-lg border border-red-500/30 bg-red-500/5" data-testid="purge-test-records-section">
          <header className="flex items-center justify-between border-b border-red-500/20 px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-red-600 flex items-center gap-1.5"><Trash2 className="h-3.5 w-3.5" /> Purge Test Records (founder-only)</div>
            <button data-testid="purge-records-preview-btn" disabled={previewingPurgeRecords} onClick={previewPurgeRecords}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
              {previewingPurgeRecords ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Scan for orphaned test records
            </button>
          </header>
          <div className="px-4 py-3 text-xs text-[#57534E]">
            Different from Purge Test Data above: this covers test-named freelancers/clients/projects/build opportunities/assignments/collaboration applications/community posts &amp; comments/users that have <span className="font-mono">no linked Notion page</span> and aren't referenced by any real record — exactly the rows a stray test run against the wrong host can leave behind. Anything else is reported as skipped with a reason and is never deleted.
          </div>
          {purgeRecordsPreview && (
            <div className="border-t border-red-500/10 px-4 py-3 space-y-3" data-testid="purge-records-preview-results">
              <div className="text-sm text-[#1C1C1E]">Found <span className="font-semibold" data-testid="purge-records-total-count">{purgeRecordsPreview.total_eligible}</span> eligible record(s) to purge</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(purgeRecordsPreview.breakdown).map(([entity, info]) => (
                  <div key={entity} className="rounded-md bg-[#F5F5F4] px-2.5 py-2" data-testid={`purge-records-breakdown-${entity}`}>
                    <div className="text-[10px] uppercase tracking-wider text-[#A8A29E]">{entity}</div>
                    <div className="text-sm text-[#1C1C1E]">{info.eligible.length} eligible{info.skipped.length > 0 ? `, ${info.skipped.length} skipped` : ""}</div>
                  </div>
                ))}
              </div>
              {Object.entries(purgeRecordsPreview.breakdown).some(([, info]) => info.skipped.length > 0) && (
                <div className="space-y-1" data-testid="purge-records-skipped-list">
                  <div className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Skipped (never deleted)</div>
                  {Object.entries(purgeRecordsPreview.breakdown).flatMap(([entity, info]) => info.skipped.map((row) => (
                    <div key={`${entity}-${row.id}`} className="flex items-center justify-between rounded-md bg-[#F5F5F4] px-2.5 py-1.5 text-xs text-[#57534E]">
                      <span className="font-mono truncate">{entity}: {row.name}</span>
                      <span className="text-[#A8A29E] ml-2 shrink-0">{row.reason}</span>
                    </div>
                  )))}
                </div>
              )}
              {purgeRecordsPreview.total_eligible > 0 && (
                <div className="flex items-center gap-2 pt-1">
                  <input
                    data-testid="purge-records-typed-count-input"
                    type="text"
                    inputMode="numeric"
                    value={purgeRecordsTypedCount}
                    onChange={(e) => setPurgeRecordsTypedCount(e.target.value)}
                    placeholder={`Type ${purgeRecordsPreview.total_eligible} to confirm`}
                    className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-xs text-[#1C1C1E] w-48"
                  />
                  <button data-testid="purge-records-confirm-btn" disabled={purgingRecords || parseInt(purgeRecordsTypedCount, 10) !== purgeRecordsPreview.total_eligible} onClick={runPurgeRecords}
                    className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                    {purgingRecords ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} {purgingRecords ? "Purging…" : `Permanently delete ${purgeRecordsPreview.total_eligible} record(s)`}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      )}


      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="admin-freelancers-section">
        <header className="border-b border-[#E7E5E4] px-4 py-3 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E] flex items-center gap-1.5"><UserCheck2 className="h-3.5 w-3.5" /> NJM Build Freelancers — manage the full roster, filters, and pagination on its own page</div>
          <Link to="/admin/freelancers" data-testid="admin-manage-freelancers-link" className="inline-flex items-center gap-1.5 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-3 py-1.5 text-xs font-medium text-[#B45309] hover:bg-[#FFC817]/20">
            <UsersRound className="h-3.5 w-3.5" /> Manage Freelancers <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </header>
      </section>

      {(user?.role === "founder" || user?.role === "partner") && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="admin-email-templates-section">
          <header className="border-b border-[#E7E5E4] px-4 py-3 flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-[#A8A29E] flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email Templates & Voice Samples — AI prompt skeletons + founder-curated sample emails, per Draft Email template</div>
            <Link to="/admin/email-templates" data-testid="admin-manage-email-templates-link" className="inline-flex items-center gap-1.5 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-3 py-1.5 text-xs font-medium text-[#B45309] hover:bg-[#FFC817]/20">
              <Mail className="h-3.5 w-3.5" /> Manage Templates <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </header>
        </section>
      )}


      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="admin-partners-section">
        <header className="border-b border-[#E7E5E4] px-4 py-3 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E] flex items-center gap-1.5"><Handshake className="h-3.5 w-3.5" /> Production Partners ({partnerRows.length}) — read-only this pass</div>
          <Link to="/admin/partner-rate-cards" data-testid="admin-manage-rate-cards-link" className="inline-flex items-center gap-1.5 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-3 py-1.5 text-xs font-medium text-[#B45309] hover:bg-[#FFC817]/20">
            <Wallet className="h-3.5 w-3.5" /> Manage Rate Cards <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </header>
        <div className="divide-y divide-[#E7E5E4]">
          {partnerRows.length === 0 && <div className="px-4 py-6 text-center text-xs text-[#A8A29E]">No production partners yet</div>}
          {partnerRows.map((p) => (
            <div key={p.id} data-testid={`partner-row-${p.id}`} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm text-[#1C1C1E]">
                  {p.name}
                  <span className="text-[10px] text-[#A8A29E] font-mono">{p.login_emails || "—"}</span>
                </div>
                <div className="text-[10px] text-[#A8A29E] mt-0.5 uppercase tracking-wider">{p.assigned_projects} assigned project{p.assigned_projects === 1 ? "" : "s"}</div>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider shrink-0 ${p.active ? "bg-emerald-500/10 text-emerald-600" : "bg-[#F5F5F4] text-[#A8A29E]"}`}>{p.active ? "Active" : "Inactive"}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-blue-500/30 bg-blue-500/[0.03]" data-testid="admin-test-accounts-section">
        <header className="border-b border-blue-500/10 px-4 py-3">
          <div className="text-xs uppercase tracking-wider text-blue-700 flex items-center gap-1.5"><FlaskConical className="h-3.5 w-3.5" /> Test Accounts ({testAccountRows.length}) — real, functional accounts quarantined from directory/dashboard counts, never mixed with real users above</div>
        </header>
        <div className="divide-y divide-blue-500/10">
          {testAccountRows.length === 0 && <div className="px-4 py-6 text-center text-xs text-[#A8A29E]">No test accounts tagged</div>}
          {testAccountRows.map((t) => (
            <div key={t.id} data-testid={`test-account-row-${t.id}`} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm text-[#1C1C1E]">
                  {t.name}
                  <span className="text-[10px] text-[#A8A29E] font-mono">{t.registered_gmail || "—"}</span>
                  <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-blue-700">Test Account</span>
                </div>
                <div className="text-[10px] text-[#A8A29E] mt-0.5 uppercase tracking-wider">Role simulated: freelancer ({t.primary_role || "—"}) · {t.archived ? "Archived" : "Active"}</div>
              </div>
              <Link to={`/build/f/${t.id}`} data-testid={`test-account-manage-link-${t.id}`} className="inline-flex items-center gap-1 rounded-md border border-[#E7E5E4] px-2.5 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] shrink-0">
                <ExternalLink className="h-3 w-3" /> Manage
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="apply-v2-toggle-section">
        <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">NJM Build Application Form v2 — production cutover switch</div>
        </header>
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <div className="text-sm text-[#1C1C1E]">Send new applicants to the new form at <span className="font-mono text-[11px]">/build/apply/v2</span></div>
            <div className="text-[10px] text-[#A8A29E] mt-0.5">Off = the current form (/build/apply) stays live and default. On = the new one goes live everywhere the "Recommended... " nav/apply link points. Same one-record submission underneath either way.</div>
          </div>
          <button
            data-testid="toggle-apply-v2"
            disabled={applyV2Enabled === null || togglingApplyV2}
            onClick={toggleApplyV2}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1 text-xs disabled:opacity-50 ${applyV2Enabled ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-600" : "border-[#E7E5E4] text-[#A8A29E]"}`}
          >
            <Power className="h-3 w-3" /> {togglingApplyV2 ? "Updating…" : applyV2Enabled ? "Live" : "Off"}
          </button>
        </div>
      </section>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="client-portal-access-section">
        <header className="flex flex-col gap-3 border-b border-[#E7E5E4] px-4 py-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-wider text-[#A8A29E] flex items-center gap-1.5">
              <ShieldOff className="h-3.5 w-3.5" /> Client Portal Access — {enabledPortalAccessCount} of {portalAccessList.length} client{portalAccessList.length === 1 ? "" : "s"} enabled
            </div>
          </div>
          <div className="text-[10px] text-[#A8A29E]">
            Deny-by-default pilot rollout — a client is fully unreachable (magic-link sign-in included) until flagged on here. No global "on for everyone" switch exists.
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#A8A29E]" />
            <input
              data-testid="portal-access-search-input"
              value={portalAccessSearch}
              onChange={(e) => setPortalAccessSearch(e.target.value)}
              placeholder="Search client name…"
              className="w-full max-w-xs rounded-md border border-[#E7E5E4] bg-[#FFFFFF] pl-8 pr-2.5 py-1.5 text-xs text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
            />
          </div>
        </header>
        <div className="max-h-96 overflow-y-auto divide-y divide-[#E7E5E4]">
          {filteredPortalAccess.length === 0 && <div className="px-4 py-6 text-center text-xs text-[#A8A29E]">No matching clients</div>}
          {filteredPortalAccess.map((row) => (
            <div key={row.company_remote_id} data-testid={`portal-access-row-${row.company_remote_id}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <div className="text-sm text-[#1C1C1E] truncate">{row.company_name || "(unnamed)"}</div>
              <div className="flex items-center gap-2 shrink-0">
                {row.enabled && (
                  <button
                    data-testid={`portal-access-preview-${row.company_remote_id}`}
                    disabled={previewingPortalAccessId === row.company_remote_id}
                    onClick={() => previewAsClient(row)}
                    title="Open the real Client Portal as this client, in a new tab"
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-2.5 py-1 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50"
                  >
                    <Eye className="h-3 w-3" /> {previewingPortalAccessId === row.company_remote_id ? "Opening…" : "Preview as client"}
                  </button>
                )}
                <button
                  data-testid={`portal-access-toggle-${row.company_remote_id}`}
                  disabled={togglingPortalAccessId === row.company_remote_id}
                  onClick={() => togglePortalAccess(row)}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1 text-xs disabled:opacity-50 ${row.enabled ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-600" : "border-[#E7E5E4] text-[#A8A29E]"}`}
                >
                  <Power className="h-3 w-3" /> {togglingPortalAccessId === row.company_remote_id ? "Updating…" : row.enabled ? "Enabled" : "Off"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]">
        <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Modules — enabling/disabling changes nav + API surface with no code change</div>
        </header>
        <div className="divide-y divide-[#E7E5E4]">
          {modules.map((m) => (
            <div key={m.key} className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="text-sm text-[#1C1C1E]">{m.name} <span className="text-[10px] text-[#A8A29E] font-mono ml-2">{m.key}</span></div>
                <div className="text-[10px] text-[#A8A29E] mt-0.5">scopes: {m.required_scopes.join(", ") || "—"} · events: {m.events_published.join(", ") || "—"}</div>
              </div>
              <button
                data-testid={`toggle-${m.key}`}
                onClick={() => toggle(m.key, !m.enabled)}
                className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1 text-xs ${m.enabled ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-600" : "border-[#E7E5E4] text-[#A8A29E]"}`}
              >
                <Power className="h-3 w-3" /> {m.enabled ? "Enabled" : "Disabled"}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="sync-health-card">
          <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Sync Health</div>
            <div className="flex gap-2">
              <button data-testid="run-delta" disabled={busy} onClick={() => runSync("delta")} className="text-xs rounded-md border border-[#E7E5E4] px-2 py-1 hover:border-[#FFC817]"><RefreshCw className="inline h-3 w-3 mr-1" />Refresh</button>
              <button data-testid="run-reconcile" disabled={busy} onClick={() => runSync("reconcile")} className="text-xs rounded-md border border-[#E7E5E4] px-2 py-1 hover:border-[#FFC817]">Full reconcile</button>
              {isFounder && <button data-testid="simulate-conflict" onClick={simulateConflict} className="text-xs rounded-md border border-orange-500/30 bg-orange-500/5 text-orange-600 px-2 py-1"><AlertTriangle className="inline h-3 w-3 mr-1" />Simulate conflict</button>}
            </div>
          </header>
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2" data-testid="sync-health-summary">
              <span className={`h-2 w-2 rounded-full ${unhealthyCount === 0 ? "bg-emerald-500" : "bg-orange-500"}`} />
              <span className="text-sm text-[#1C1C1E]">
                {unhealthyCount === 0 ? "All sources healthy" : `${unhealthyCount} of ${dbEntries.length} source${dbEntries.length === 1 ? "" : "s"} need attention`}
              </span>
              <span className="text-[10px] text-[#A8A29E]">· last synced {relativeTime(health?.engine?.last_delta_at)}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <ConnectorChip label="Gmail — Info" connected={!!gmailStatus?.connections?.info?.connected} testId="connector-chip-gmail-info" />
              <ConnectorChip label="Gmail — Finance" connected={!!gmailStatus?.connections?.finance?.connected} testId="connector-chip-gmail-finance" />
              <ConnectorChip label="Zoho" connected={!!zohoStatus?.connected} testId="connector-chip-zoho" />
              <ConnectorChip label="Notion" connected={notionConnected} testId="connector-chip-notion" />
            </div>
            <div className="grid grid-cols-3 gap-2 pt-1">
              {Object.entries(readModel).map(([entity, stats]) => {
                const pendingTotal = (stats.pending_create || 0) + (stats.failed_needs_attention || 0);
                return (
                <div key={entity} className="rounded-md bg-[#F5F5F4] px-2.5 py-2" data-testid={`read-model-${entity}`}>
                  <div className="text-[10px] uppercase tracking-wider text-[#A8A29E]">{entity}</div>
                  <div className="text-sm text-[#1C1C1E]">{stats.total} {stats.stale > 0 && <span className="text-orange-600 text-[10px]">· {stats.stale} stale</span>}</div>
                  {pendingTotal > 0 && (
                    <button
                      data-testid={`pending-create-toggle-${entity}`}
                      onClick={() => togglePendingCreate(entity)}
                      className="mt-0.5 text-[10px] text-sky-700 hover:underline"
                    >
                      {pendingTotal} not in Notion{pendingTotal === 1 ? "" : "s"} · {expandedPendingEntity === entity ? "Hide" : "View"}
                    </button>
                  )}
                </div>
                );
              })}
            </div>
            {expandedPendingEntity && (
              <div className="rounded-md border border-sky-500/20 bg-sky-500/5 p-2.5 space-y-1" data-testid="pending-create-panel">
                {loadingPendingCreate ? (
                  <div className="text-[11px] text-[#A8A29E]">Loading…</div>
                ) : (pendingCreate?.[expandedPendingEntity] || []).length === 0 ? (
                  <div className="text-[11px] text-[#A8A29E]" data-testid="pending-create-empty">None found — they likely synced since the count above was cached.</div>
                ) : (
                  (pendingCreate[expandedPendingEntity] || []).map((row) => (
                    <div key={row.id} className="flex items-start justify-between gap-2 border-b border-sky-500/10 pb-1 last:border-b-0 last:pb-0">
                      <div className="min-w-0">
                        <Link
                          to={`${PENDING_CREATE_ENTITY_ROUTE[expandedPendingEntity]}/${row.id}`}
                          data-testid={`pending-create-row-${row.id}`}
                          className="block text-[11px] text-[#1C1C1E] hover:text-[#B45309] hover:underline truncate"
                        >
                          {row.name}
                        </Link>
                        {row.last_error && <div className="text-[10px] text-[#A8A29E] truncate" data-testid={`pending-create-row-${row.id}-error`}>{row.sync_state === "failed_needs_attention" ? "Gave up: " : "Last error: "}{row.last_error}</div>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="ai-gateway-card">
          <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-[#A8A29E]">AI Gateway</div>
            <button data-testid="reload-ai" onClick={reloadAi} className="text-xs rounded-md border border-[#E7E5E4] px-2 py-1 hover:border-[#FFC817]">Reload config</button>
          </header>
          {aiUsage && (
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-[#B45309]" />
                <span className="text-sm text-[#1C1C1E]">Connected — using {aiUsage.config.default_provider}</span>
              </div>
              <div className="text-xs text-[#57534E]">{aiUsage.log.length} call{aiUsage.log.length === 1 ? "" : "s"} logged this session</div>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="stuck-sync-section">
        <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E] flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Stuck Films/Deliverables & Projects Notion Sync ({stuckSync.total})
          </div>
          <button onClick={refreshStuckSync} data-testid="stuck-sync-refresh-btn" className="text-[10px] text-[#A8A29E] hover:text-[#1C1C1E]">Refresh</button>
        </header>
        <div className="px-4 py-3 space-y-3">
          <p className="text-xs text-[#57534E]" data-testid="stuck-sync-note">
            Records the background retry queue has given up on ({stuckSync.gave_up_after}+ attempts — “needs founder review”). Retry re-attempts the same Notion write once and shows the real result. It does not recreate a deleted Notion page. One record, one click — no bulk action.
          </p>
          {stuckSync.items.length === 0 ? (
            <div className="text-xs text-[#A8A29E]" data-testid="stuck-sync-empty">No stuck Films/Deliverables sync records.</div>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto" data-testid="stuck-sync-list">
              {stuckSync.items.map((row) => (
                <div key={row.id} data-testid={`stuck-sync-row-${row.id}`} className="flex items-center justify-between gap-3 text-xs py-2 border-b border-[#F5F5F4] last:border-0">
                  <div className="min-w-0 flex-1">
                    <div className="text-[#1C1C1E] truncate">{row.deliverable_name || "(record missing)"} <span className="text-[#A8A29E] font-mono">{String(row.id).slice(0, 8)}</span></div>
                    <div className="text-[#A8A29E] truncate">
                      {row.project_names?.length ? row.project_names.join(" · ") : "No linked project"}
                      {" · "}{row.attempts} attempts · last tried {relativeTime(row.last_tried_at)}
                    </div>
                    {stuckRetryResult?.id === row.id && (
                      <div data-testid={`stuck-sync-retry-result-${row.id}`} className={`mt-1 ${stuckRetryResult.ok ? "text-emerald-600" : "text-red-600"}`}>
                        {stuckRetryResult.ok ? "Retry succeeded — removed from the queue." : `Retry failed: ${stuckRetryResult.error_reason || stuckRetryResult.error || "unknown"}`}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    <span
                      data-testid={`stuck-sync-reason-${row.id}`}
                      className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${row.error_reason === "other" ? "bg-[#F5F5F4] text-[#57534E]" : "bg-red-500/10 text-red-600"}`}
                    >
                      {(row.error_reason || "other").replace(/_/g, " ")}
                    </span>
                    {row.sync_state === "failed_needs_attention" && (
                      <span data-testid={`stuck-sync-needs-attention-${row.id}`} className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider bg-red-600/10 text-red-700 font-semibold">
                        needs attention — retry stopped
                      </span>
                    )}
                    {!row.recoverable && (
                      <span data-testid={`stuck-sync-unrecoverable-${row.id}`} className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-700">
                        retry won't fix
                      </span>
                    )}
                    {isFounder ? (
                      <button
                        data-testid={`stuck-sync-retry-btn-${row.id}`}
                        disabled={retryingStuckId === row.id}
                        onClick={() => retryStuckSync(row)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-2.5 py-1 text-[10px] text-[#B45309] hover:bg-[#FFC817]/20 disabled:opacity-50"
                      >
                        {retryingStuckId === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Retry now
                      </button>
                    ) : (
                      <div data-testid={`stuck-sync-founder-only-${row.id}`} className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Founder only</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GmailConnectionCard
          connection="info" title="Gmail — General (info@)" mailboxLabel="Email Generator + Commercial proposals"
          status={gmailStatus?.connections?.info} configured={gmailStatus?.configured} isFounder={isFounder}
          onConnect={() => connectGmail("info")}
          extraAction={gmailStatus?.connections?.info?.connected && (
            <button data-testid="refresh-email-activity-btn" disabled={busy} onClick={refreshEmailActivity}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh Email Activity
            </button>
          )}
        />
        <GmailConnectionCard
          connection="finance" title="Gmail — Statements (finance@)" mailboxLabel="Freelancer payment statements (Phase 6) — never info@"
          status={gmailStatus?.connections?.finance} configured={gmailStatus?.configured} isFounder={isFounder}
          onConnect={() => connectGmail("finance")}
        />
      </section>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]">
        <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Zoho Books (read-only — invoices/contacts/payments only)</div>
        </header>
        <div className="px-4 py-3 flex items-center justify-between">
          {zohoStatus?.connected ? (
            <div className="flex items-center gap-1.5 text-xs text-emerald-600" data-testid="zoho-connected-badge"><CheckCircle2 className="h-3.5 w-3.5" /> Connected — Receivables can refresh live Zoho data</div>
          ) : (
            <div className="text-xs text-[#57534E]">{zohoStatus?.configured ? "Not connected yet" : "ZOHO_CLIENT_ID/SECRET not configured"}</div>
          )}
          {zohoStatus?.configured && !zohoStatus?.connected && (
            isFounder ? (
              <button data-testid="connect-zoho-btn" onClick={connectZoho} className="inline-flex items-center gap-1.5 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-3 py-1.5 text-xs text-[#B45309] hover:bg-[#FFC817]/20">
                <Wallet className="h-3.5 w-3.5" /> Connect Zoho
              </button>
            ) : (
              <div data-testid="connect-zoho-founder-only" className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Founder only</div>
            )
          )}
        </div>
      </section>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]">
        <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">YouTube Channel Sync (read-only — Phase 2, nikhil@njmpro.com)</div>
        </header>
        <div className="px-4 py-3 flex items-center justify-between">
          {youtubeChannelStatus?.connected ? (
            <div className="flex items-center gap-1.5 text-xs text-emerald-600" data-testid="youtube-channel-connected-badge"><CheckCircle2 className="h-3.5 w-3.5" /> Connected — new uploads (incl. unlisted) sync daily as "Needs Review"</div>
          ) : (
            <div className="text-xs text-[#57534E]">{youtubeChannelStatus?.configured ? "Not connected yet" : "GMAIL_CLIENT_ID/SECRET or YOUTUBE_CHANNEL_REDIRECT_URI not configured"}</div>
          )}
          <div className="flex items-center gap-2">
            {youtubeChannelStatus?.connected && (
              <button data-testid="sync-youtube-channel-btn" disabled={syncingYoutube} onClick={syncYoutubeChannelNow}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
                {syncingYoutube ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync Now
              </button>
            )}
            {youtubeChannelStatus?.configured && !youtubeChannelStatus?.connected && (
              isFounder ? (
                <button data-testid="connect-youtube-channel-btn" onClick={connectYoutubeChannel} className="inline-flex items-center gap-1.5 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-3 py-1.5 text-xs text-[#B45309] hover:bg-[#FFC817]/20">
                  <Youtube className="h-3.5 w-3.5" /> Connect YouTube Channel
                </button>
              ) : (
                <div data-testid="connect-youtube-channel-founder-only" className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Founder only</div>
              )
            )}
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="library-cleanup-section">
        <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Master Library Cleanup — Dry Run (Phase 3, read-only)</div>
          <button data-testid="run-library-cleanup-dry-run-btn" disabled={cleanupRunning} onClick={runLibraryCleanupDryRun}
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
            {cleanupRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Run Dry-Run Report
          </button>
        </header>
        <div className="px-4 py-3 space-y-3">
          <p className="text-xs text-[#57534E]">Reports personal-channel rows (@WhenOutofFrame), duplicate links, dead links, and stale "Needs Review" rows. Never archives or deletes anything — review below, then tell the founder whether to proceed with archiving.</p>
          {cleanupReport && (
            <div data-testid="library-cleanup-report" className="space-y-3">
              <div className="text-[11px] text-[#A8A29E]">Checked {cleanupReport.total_rows} row(s) at {new Date(cleanupReport.checked_at).toLocaleString()} — {cleanupReport.in_scope_row_count} in scope after excluding personal-channel rows</div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] mb-1">Personal channel ({cleanupReport.personal_channel_handle}) — {cleanupReport.personal_channel_count}</div>
                {cleanupReport.personal_channel_count === 0 ? <div className="text-xs text-emerald-600">None found</div> : (
                  <div data-testid="cleanup-personal-channel-list" className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                    {cleanupReport.personal_channel_rows.map((r) => (
                      <div key={r.id} data-testid={`cleanup-personal-channel-${r.id}`} className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs">{r.name} <span className="text-[#A8A29E]">— {r.status}</span></div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] mb-1">Duplicate links — {cleanupReport.duplicate_extra_row_count} extra row(s)</div>
                {cleanupReport.duplicates.length === 0 ? <div className="text-xs text-emerald-600">None found</div> : (
                  <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                    {cleanupReport.duplicates.map((d) => (
                      <div key={d.video_id} data-testid={`cleanup-duplicate-${d.video_id}`} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
                        <span className="text-[#A8A29E]">video {d.video_id}:</span>{" "}
                        {d.rows.map((r, i) => (
                          <span key={r.id}>{i > 0 && " · "}<span className={r.keep ? "font-semibold text-emerald-700" : ""}>{r.name}{r.keep ? " (keep)" : ""}</span></span>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] mb-1">Dead links — {cleanupReport.dead_links?.length ?? 0}</div>
                {cleanupReport.dead_link_check_error ? (
                  <div data-testid="cleanup-dead-link-check-error" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">Couldn't check — dead link count below may be incomplete: {cleanupReport.dead_link_check_error}</div>
                ) : cleanupReport.dead_links.length === 0 ? <div className="text-xs text-emerald-600">None found</div> : (
                  <div className="space-y-1.5">
                    {cleanupReport.dead_links.map((r) => (
                      <div key={r.id} data-testid={`cleanup-dead-link-${r.id}`} className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs">{r.name}</div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] mb-1">Stale "Needs Review" ({cleanupReport.stale_needs_review_days}+ days) — {cleanupReport.stale_needs_review.length}</div>
                {cleanupReport.stale_needs_review.length === 0 ? <div className="text-xs text-emerald-600">None found</div> : (
                  <div className="space-y-1.5">
                    {cleanupReport.stale_needs_review.map((r) => (
                      <div key={r.id} data-testid={`cleanup-stale-${r.id}`} className="rounded-md border border-[#E7E5E4] px-3 py-2 text-xs">{r.name} <span className="text-[#A8A29E]">— added {r.created_at?.slice(0, 10)}</span></div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="onboarding-emails-section">
        <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Emails & Email Audit (Resend — build@build.njmpro.com)</div>
        </header>
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center justify-between">
            {emailHealth?.configured ? (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600" data-testid="onboarding-emails-connected-badge">
                <CheckCircle2 className="h-3.5 w-3.5" /> Connected — sender {emailHealth.sender}, reply-to {emailHealth.reply_to}
              </div>
            ) : (
              <div className="text-xs text-[#57534E]" data-testid="onboarding-emails-not-configured">RESEND_API_KEY not configured</div>
            )}
            {emailHealth?.configured && !emailHealth?.webhook_secret_configured && (
              <div className="text-[10px] uppercase tracking-wider text-orange-600" data-testid="onboarding-emails-webhook-warning">Webhook secret not set — delivery status won't sync to Notion</div>
            )}
          </div>

          {isFounder && emailHealth?.configured && (
            <div className="flex items-center gap-2 pt-1" data-testid="onboarding-emails-test-send">
              <select data-testid="onboarding-emails-test-template" value={testSendTemplate} onChange={(e) => setTestSendTemplate(e.target.value)}
                className="rounded-md border border-[#E7E5E4] px-2 py-1.5 text-xs">
                <option value="calendly_invite">Calendly invite</option>
                <option value="welcome_onboarding">Welcome / onboarding</option>
                <option value="reminder">Reminder</option>
                <option value="decline">Decline</option>
                <option value="application_received">Application received (Ticket 2)</option>
                <option value="request_work_links">Request work links (Ticket 3)</option>
              </select>
              <input data-testid="onboarding-emails-test-recipient" type="email" placeholder="test@gmail.com" value={testSendTo}
                onChange={(e) => setTestSendTo(e.target.value)} className="flex-1 rounded-md border border-[#E7E5E4] px-2 py-1.5 text-xs" />
              <button data-testid="onboarding-emails-test-send-btn" disabled={sendingTest} onClick={sendTestEmail}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-3 py-1.5 text-xs text-[#B45309] hover:bg-[#FFC817]/20 disabled:opacity-50">
                {sendingTest ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Send test
              </button>
            </div>
          )}

          <div className="border-t border-[#E7E5E4] pt-2">
            {/* Founder Email Audit (2026-02, email-leak incident remediation): every outcome
                the non-production send-gate (kernel/email_gateway.py::send()) has produced,
                across EVERY email path in the app — including client_magic_link rows now that
                the client-portal blind spot is closed. Summary badges are clickable filters so
                "how many real sends did the gate block this week" is a single glance, not a
                Mongo query. */}
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Email Audit — send log ({sendLog.length}{sendLogTotal > sendLog.length ? ` of ${sendLogTotal}` : ""})</div>
              <div className="flex items-center gap-2">
                <select value={sendLogTemplateFilter} onChange={(e) => setSendLogTemplateFilter(e.target.value)} data-testid="email-audit-template-filter"
                  className="rounded-md border border-[#E7E5E4] px-2 py-1 text-[10px] uppercase tracking-wider text-[#57534E]">
                  <option value="all">All templates</option>
                  {[...new Set(sendLog.map((r) => r.template))].sort().map((t) => (
                    <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                  ))}
                </select>
                <button onClick={refreshSendLog} data-testid="email-audit-refresh-btn" className="text-[10px] text-[#A8A29E] hover:text-[#1C1C1E]">Refresh</button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="email-audit-summary">
              {Object.entries(sendLogSummary).sort((a, b) => b[1] - a[1]).map(([outcome, count]) => (
                <button key={outcome} onClick={() => setSendLogOutcomeFilter((prev) => (prev === outcome ? null : outcome))}
                  data-testid={`email-audit-summary-badge-${outcome}`}
                  className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider border transition-colors ${sendLogOutcomeFilter === outcome ? "border-[#1C1C1E] bg-[#1C1C1E] text-white" : `${outcomeBadgeCls(outcome)} border-transparent`}`}>
                  {outcome.replace(/_/g, " ")} · {count}
                </button>
              ))}
            </div>
            {sendLog.length === 0 ? (
              <div className="text-xs text-[#A8A29E]" data-testid="onboarding-emails-log-empty">No emails sent yet.</div>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="onboarding-emails-log-list">
                {sendLog.filter((row) => (!sendLogOutcomeFilter || row.outcome === sendLogOutcomeFilter) && (sendLogTemplateFilter === "all" || row.template === sendLogTemplateFilter)).length === 0 ? (
                  <div className="text-xs text-[#A8A29E]" data-testid="onboarding-emails-log-no-match">No emails match this filter.</div>
                ) : sendLog.filter((row) => (!sendLogOutcomeFilter || row.outcome === sendLogOutcomeFilter) && (sendLogTemplateFilter === "all" || row.template === sendLogTemplateFilter)).map((row) => (
                  <div key={row.id} data-testid={`onboarding-emails-log-row-${row.id}`} className="flex items-center justify-between text-xs py-1 border-b border-[#F5F5F4] last:border-0">
                    <div className="min-w-0 flex-1">
                      <span className="text-[#1C1C1E]">{row.recipient}</span>
                      <span className="text-[#A8A29E]"> · {row.template} · {row.sent_by ? `by ${row.sent_by} · ` : ""}{relativeTime(row.sent_at)}</span>
                    </div>
                    <span className={`shrink-0 ml-2 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${outcomeBadgeCls(row.outcome)}`}>{(row.outcome || "unknown").replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {canApproveEmails && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="email-approvals-section">
          <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-[#A8A29E]">
              Email Approvals — members' collaboration blasts pending review ({emailApprovals.length})
            </div>
            <button data-testid="email-approvals-refresh-btn" onClick={refreshEmailApprovals} disabled={loadingApprovals}
              className="text-[#A8A29E] hover:text-[#1C1C1E]">
              <RefreshCw className={`h-3.5 w-3.5 ${loadingApprovals ? "animate-spin" : ""}`} />
            </button>
          </header>
          <div className="px-4 py-3 space-y-2">
            {emailApprovals.length === 0 ? (
              <div className="text-xs text-[#A8A29E]" data-testid="email-approvals-empty">Nothing pending — every member post's email either already sent or has no eligible recipients.</div>
            ) : emailApprovals.map((a) => (
              <div key={a.id} data-testid={`email-approval-row-${a.id}`} className="rounded-md border border-[#E7E5E4] px-3 py-2 text-xs space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="text-[#1C1C1E] font-medium">{a.subject}</span>
                    <span className="text-[#A8A29E]"> · {a.count} recipient{a.count === 1 ? "" : "s"} · posted by {a.actor_name} ({a.actor_role}) · {relativeTime(a.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button data-testid={`email-approval-reject-${a.id}`} disabled={approvalActionBusyId === a.id} onClick={() => rejectEmail(a.id)}
                      className="inline-flex items-center gap-1 rounded-md border border-[#E7E5E4] px-2.5 py-1 text-[11px] text-[#57534E] hover:border-red-400 hover:text-red-600 disabled:opacity-50">
                      <XCircle className="h-3 w-3" /> Reject
                    </button>
                    <button data-testid={`email-approval-approve-${a.id}`} disabled={approvalActionBusyId === a.id} onClick={() => approveEmail(a.id)}
                      className="inline-flex items-center gap-1 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-2.5 py-1 text-[11px] text-[#B45309] hover:bg-[#FFC817]/20 disabled:opacity-50">
                      {approvalActionBusyId === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Approve
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {isFounder && (
        <section className="rounded-lg border border-red-500/30 bg-red-500/5" data-testid="purge-test-data-section">
          <header className="flex items-center justify-between border-b border-red-500/20 px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-red-600 flex items-center gap-1.5"><Trash2 className="h-3.5 w-3.5" /> Purge Test Data (founder-only)</div>
            <button data-testid="purge-preview-btn" disabled={previewingPurge} onClick={previewPurge}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
              {previewingPurge ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Scan for TEST_/ZZZTEST_ fixtures
            </button>
          </header>
          <div className="px-4 py-3 text-xs text-[#57534E]">
            Clears Mongo-only leftovers (draft quotes, generated SOWs, briefs, tasks) named <span className="font-mono">TEST_</span>/<span className="font-mono">ZZZTEST_</span> — never touches Notion-backed records (clients/projects/contacts/etc.), which are cleaned up directly in Notion.
          </div>
          {purgePreview && (
            <div className="border-t border-red-500/10 px-4 py-3 space-y-2" data-testid="purge-preview-results">
              <div className="text-sm text-[#1C1C1E]">Found <span className="font-semibold" data-testid="purge-total-count">{purgePreview.total}</span> fixture(s) to purge</div>
              {purgePreview.total > 0 && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {Object.entries(purgePreview.breakdown).map(([entity, info]) => (
                      <div key={entity} className="rounded-md bg-[#F5F5F4] px-2.5 py-2" data-testid={`purge-breakdown-${entity}`}>
                        <div className="text-[10px] uppercase tracking-wider text-[#A8A29E]">{entity}</div>
                        <div className="text-sm text-[#1C1C1E]">{info.count}</div>
                      </div>
                    ))}
                  </div>
                  <button data-testid="purge-confirm-btn" disabled={purging} onClick={runPurge}
                    className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">
                    {purging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} {purging ? "Purging…" : `Permanently delete ${purgePreview.total} fixture(s)`}
                  </button>
                </>
              )}
            </div>
          )}
        </section>
      )}

      {isFounder && (
        <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="onboarding-migration-section">
          <header className="border-b border-[#E7E5E4] px-4 py-3 text-xs uppercase tracking-wider text-[#A8A29E] flex items-center gap-1.5">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Onboarding Migration (founder-only)
          </header>
          <div className="p-4 space-y-4">
            <p className="text-xs text-[#57534E]">
              Upload the legacy "Staff on-boarding form" CSV export. Dry-run only ever reads —
              nothing is written until you explicitly commit, and commit only ever writes the
              MATCHED bucket. Ambiguous/legacy-no-account/account-no-legacy rows are never
              auto-attached.
            </p>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FAFAF9] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] cursor-pointer">
                <Upload className="h-3.5 w-3.5" />
                {migrationFile ? migrationFile.name : "Choose CSV file"}
                <input type="file" accept=".csv" className="hidden" data-testid="onboarding-migration-file-input"
                  onChange={(e) => { setMigrationFile(e.target.files?.[0] || null); setMigrationDryRun(null); }} />
              </label>
              <button data-testid="onboarding-migration-dry-run-btn" disabled={!migrationFile || runningMigrationDryRun} onClick={runMigrationDryRun}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
                {runningMigrationDryRun ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Run dry-run
              </button>
            </div>

            {migrationDryRun && (
              <div className="space-y-3 border-t border-[#E7E5E4] pt-3" data-testid="onboarding-migration-dry-run-results">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    ["matched", "Matched"], ["ambiguous", "Ambiguous"],
                    ["legacy_no_account", "Legacy, no account"], ["account_no_legacy", "Account, no legacy"],
                  ].map(([key, label]) => (
                    <div key={key} className="rounded-md bg-[#F5F5F4] px-2.5 py-2" data-testid={`onboarding-migration-bucket-${key}`}>
                      <div className="text-[10px] uppercase tracking-wider text-[#A8A29E]">{label}</div>
                      <div className="text-lg font-semibold text-[#1C1C1E]">{migrationDryRun.summary[key]}</div>
                    </div>
                  ))}
                </div>

                {migrationDryRun.ambiguous.length > 0 && (
                  <div className="space-y-1.5" data-testid="onboarding-migration-ambiguous-list">
                    <div className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Ambiguous — manual review only, never auto-attached</div>
                    {migrationDryRun.ambiguous.map((row, i) => (
                      <div key={i} data-testid={`onboarding-migration-ambiguous-row-${i}`} className="rounded-md border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-xs">
                        <div className="text-[#1C1C1E]">{row.email}</div>
                        <div className="text-[#57534E]">
                          {row.reason === "multiple_freelancer_accounts_same_email" && "Multiple freelancer accounts share this email"}
                          {row.reason === "multiple_legacy_rows_same_email" && `Multiple legacy rows for ${row.freelancer_name || "this freelancer"} (rows ${(row.row_numbers || []).join(", ")})`}
                          {row.reason === "name_or_phone_conflict" && `Name/phone conflict vs ${row.freelancer_name || "account"} (legacy: "${row.legacy_name || "—"}", ${row.legacy_phone || "—"})`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button data-testid="onboarding-migration-commit-btn" disabled={committingMigration || migrationDryRun.summary.matched === 0} onClick={commitMigration}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#1C1C1E] px-3 py-1.5 text-xs font-medium text-white hover:bg-black disabled:opacity-50">
                  {committingMigration ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  {committingMigration ? "Committing…" : `Commit matched (${migrationDryRun.summary.matched})`}
                </button>
              </div>
            )}

            <div className="border-t border-[#E7E5E4] pt-3">
              <button data-testid="onboarding-migration-log-toggle" onClick={() => (migrationLog ? setMigrationLog(null) : loadMigrationLog())}
                disabled={loadingMigrationLog} className="text-xs text-[#57534E] hover:text-[#1C1C1E] flex items-center gap-1.5">
                {loadingMigrationLog ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronDown className={`h-3.5 w-3.5 transition-transform ${migrationLog ? "rotate-180" : ""}`} />}
                {migrationLog ? "Hide migration log" : "View migration log"}
              </button>
              {migrationLog && (
                <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-[#E7E5E4]" data-testid="onboarding-migration-log-list">
                  {migrationLog.length === 0 && <div className="py-3 text-xs text-[#A8A29E]">No migration activity yet</div>}
                  {migrationLog.map((row) => (
                    <div key={row.id} data-testid={`onboarding-migration-log-row-${row.id}`} className="py-2 flex items-center justify-between gap-3 text-xs">
                      <div className="text-[#1C1C1E]">{row.freelancer_name} <span className="text-[#A8A29E]">— {row.action}</span></div>
                      <div className="text-right text-[10px] text-[#A8A29E]">{row.at?.slice(0, 19).replace("T", " ")} · {row.actor_email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]">
        <header className="border-b border-[#E7E5E4] px-4 py-3 text-xs uppercase tracking-wider text-[#A8A29E]">Pending Approvals ({approvals.length})</header>
        <div className="p-4 space-y-3">
          {approvals.length === 0 && <div className="text-xs text-[#A8A29E]">No pending approvals</div>}
          {approvals.map((a) => <ApprovalCard key={a.id} approval={a} onDecided={refresh} />)}
        </div>
      </section>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="event-audit-section">
        <header className="border-b border-[#E7E5E4] px-4 py-3 text-xs uppercase tracking-wider text-[#A8A29E]">Event Audit</header>
        <div className="max-h-96 overflow-y-auto divide-y divide-[#E7E5E4]">
          {audit.length === 0 && <div className="px-4 py-6 text-center text-xs text-[#A8A29E]" data-testid="event-audit-empty">No meaningful events yet</div>}
          {audit.slice(0, 30).map((e) => (
            <div key={e.id} data-testid={`event-audit-row-${e.id}`} className="px-4 py-2.5 flex items-center justify-between gap-3 text-xs">
              <div className="min-w-0 flex-1">
                <span className="text-[#1C1C1E]">{e.action}</span>
                <span className="text-[#A8A29E]"> — {e.target}</span>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[#57534E] font-mono">{e.actor}</div>
                <div className="text-[10px] text-[#A8A29E]">{e.at?.slice(0, 19).replace("T", " ")}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF]" data-testid="advanced-diagnostics-section">
        <button
          data-testid="advanced-diagnostics-toggle"
          onClick={() => setShowDiagnostics((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-[#A8A29E] hover:text-[#1C1C1E]"
        >
          Advanced / diagnostics
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showDiagnostics ? "rotate-180" : ""}`} />
        </button>
        {showDiagnostics && (
          <div className="border-t border-[#E7E5E4] divide-y divide-[#E7E5E4]" data-testid="advanced-diagnostics-content">
            <div>
              <div className="px-4 pt-3 text-[10px] uppercase tracking-wider text-[#A8A29E]">Sync Health (raw)</div>
              <pre className="p-4 text-xs font-mono text-[#57534E] overflow-x-auto">{JSON.stringify(health, null, 2)}</pre>
            </div>
            {aiUsage && (
              <div>
                <div className="px-4 pt-3 text-[10px] uppercase tracking-wider text-[#A8A29E]">AI Gateway (raw)</div>
                <pre className="p-4 text-xs font-mono text-[#57534E] overflow-x-auto">{JSON.stringify(aiUsage, null, 2)}</pre>
              </div>
            )}
          </div>
        )}
      </section>
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="edit-user-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeEdit} data-testid="edit-user-modal-backdrop" />
          <div className="relative z-10 w-full max-w-sm rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-[#1C1C1E]">Edit user</div>
              <button data-testid="edit-user-modal-close" onClick={closeEdit} className="text-[#A8A29E] hover:text-[#1C1C1E]"><X className="h-4 w-4" /></button>
            </div>
            <div className="text-[10px] text-[#A8A29E] font-mono">{editingUser.email}</div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Display name</label>
              <input data-testid="edit-user-name-input" value={editName} onChange={(e) => setEditName(e.target.value)} className="w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Role</label>
              {editingUser.id === user.id ? (
                <div className="rounded-md bg-[#F5F5F4] px-2.5 py-1.5 text-xs text-[#57534E]" data-testid="edit-user-role-self-readonly">{editingUser.role} — you can't change your own role</div>
              ) : ROLES.includes(editingUser.role) ? (
                <select data-testid="edit-user-role-select" value={editRole} onChange={(e) => setEditRole(e.target.value)} className="w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50">
                  {ROLES.filter((r) => isFounder || (r !== "founder" && r !== "partner")).map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              ) : (
                <div className="rounded-md bg-[#F5F5F4] px-2.5 py-1.5 text-xs text-[#57534E]" data-testid="edit-user-role-readonly">{editingUser.role} — managed via NJM Build login, not editable here</div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button data-testid="edit-user-cancel-btn" onClick={closeEdit} className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817]">Cancel</button>
              <button data-testid="edit-user-save-btn" disabled={savingUser} onClick={saveEdit} className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:bg-[#FFC817]/90 disabled:opacity-50">
                {savingUser ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save
              </button>
            </div>
          </div>
        </div>
      )}
      {showCreateStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="create-staff-modal" role="dialog" aria-modal="true" aria-label="Add staff account">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeCreateStaff} data-testid="create-staff-modal-backdrop" />
          <div className="relative z-10 w-full max-w-sm rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-[#1C1C1E]">Add staff account</div>
              <button data-testid="create-staff-modal-close" onClick={closeCreateStaff} aria-label="Close" className="text-[#A8A29E] hover:text-[#1C1C1E]"><X className="h-4 w-4" /></button>
            </div>

            {createdStaffResult ? (
              <div className="space-y-3" data-testid="create-staff-success">
                <p className="text-xs text-[#57534E]">Account created for <span className="font-medium text-[#1C1C1E]">{createdStaffResult.email}</span>. {createdStaffResult.outcome === "sent" ? "An invite email is on its way to them." : createdStaffResult.outcome === "deferred_pending_confirmation" ? "Confirm the send to email their invite." : "The invite email wasn't sent in this environment — ask them to check their inbox once emails are enabled, or use \"Resend invite\" from the Users list later."}</p>
                <p className="text-[10px] text-[#A8A29E]">For their security, the set-password link is only ever emailed directly to them — it's never shown here, to you, or to anyone else with admin access.</p>
                <div className="flex justify-end pt-1">
                  <button data-testid="create-staff-done-btn" onClick={closeCreateStaff} className="rounded-md bg-[#FFC817] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:bg-[#FFC817]/90">Done</button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-[10px] text-[#A8A29E]">No password is set here — they'll get a one-time emailed link to choose their own.</p>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Full name</label>
                  <input data-testid="create-staff-name-input" value={newStaffName} onChange={(e) => setNewStaffName(e.target.value)} className="w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Login email (their own, individual address)</label>
                  <input data-testid="create-staff-email-input" type="email" value={newStaffEmail} onChange={(e) => setNewStaffEmail(e.target.value)} className="w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Role</label>
                  <select data-testid="create-staff-role-select" value={newStaffRole} onChange={(e) => setNewStaffRole(e.target.value)} className="w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50">
                    {STAFF_CREATE_ROLES.filter((r) => isFounder || (r !== "founder" && r !== "partner")).map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {!isFounder && <p className="text-[10px] text-[#A8A29E]">Only a founder can create a founder or partner account.</p>}
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button data-testid="create-staff-cancel-btn" onClick={closeCreateStaff} className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817]">Cancel</button>
                  <button data-testid="create-staff-submit-btn" disabled={creatingStaff} onClick={createStaffAccount} className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:bg-[#FFC817]/90 disabled:opacity-50">
                    {creatingStaff ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Create account
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {reassignUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="reassign-work-modal" role="dialog" aria-modal="true" aria-label="Reassign work">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeReassign} data-testid="reassign-work-modal-backdrop" />
          <div className="relative z-10 w-full max-w-md rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-5 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-[#1C1C1E]">Reassign work</div>
              <button data-testid="reassign-work-modal-close" onClick={closeReassign} aria-label="Close" className="text-[#A8A29E] hover:text-[#1C1C1E]"><X className="h-4 w-4" /></button>
            </div>

            {reassignApplyResult ? (
              <div className="space-y-3" data-testid="reassign-work-success">
                <p className="text-xs text-[#57534E]">Moved <span className="font-medium text-[#1C1C1E]">{reassignApplyResult.counts.projects} project(s)</span> and <span className="font-medium text-[#1C1C1E]">{reassignApplyResult.counts.tasks} task(s)</span> from {reassignUser.name} to the selected account.</p>
                <p className="text-[10px] text-[#A8A29E] font-mono" data-testid="reassign-work-batch-id">Batch: {reassignApplyResult.batch_id}</p>
                <div className="flex justify-end pt-1">
                  <button data-testid="reassign-work-done-btn" onClick={closeReassign} className="rounded-md bg-[#FFC817] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:bg-[#FFC817]/90">Done</button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">From</label>
                  <div className="rounded-md bg-[#F5F5F4] px-2.5 py-1.5 text-xs text-[#57534E]" data-testid="reassign-work-source">{reassignUser.name} <span className="font-mono text-[10px] text-[#A8A29E]">{reassignUser.email}</span></div>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">To</label>
                  <select data-testid="reassign-work-target-select" value={reassignTargetId} onChange={(e) => { setReassignTargetId(e.target.value); setReassignDryRun(null); }} className="w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50">
                    <option value="">Pick an active staff account…</option>
                    {users.filter((u) => u.active !== false && u.id !== reassignUser.id && STAFF_CREATE_ROLES.includes(u.role)).map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="inline-flex items-center gap-2 text-xs text-[#57534E]">
                    <input data-testid="reassign-work-include-projects" type="checkbox" checked={reassignIncludeProjects} onChange={(e) => { setReassignIncludeProjects(e.target.checked); setReassignDryRun(null); }} /> Projects (Project Manager)
                  </label>
                  <label className="inline-flex items-center gap-2 text-xs text-[#57534E]">
                    <input data-testid="reassign-work-include-tasks" type="checkbox" checked={reassignIncludeTasks} onChange={(e) => { setReassignIncludeTasks(e.target.checked); setReassignDryRun(null); }} /> Tasks (Assignee)
                  </label>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Scope</label>
                  <select data-testid="reassign-work-scope-select" value={reassignScope} onChange={(e) => { setReassignScope(e.target.value); setReassignDryRun(null); }} className="w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50">
                    <option value="open">Open items only</option>
                    <option value="all">All items</option>
                  </select>
                </div>

                {reassignDryRun && (
                  <div className="rounded-md border border-[#E7E5E4] bg-[#F5F5F4] p-3 space-y-2" data-testid="reassign-work-dry-run-summary">
                    <div className="text-xs font-medium text-[#1C1C1E]">Would move {reassignDryRun.counts.projects} project(s) and {reassignDryRun.counts.tasks} task(s)</div>
                    {reassignDryRun.counts.projects + reassignDryRun.counts.tasks === 0 && <div className="text-[10px] text-[#A8A29E]">Nothing to move for this account with these filters.</div>}
                    {reassignDryRun.projects.length > 0 && (
                      <ul className="text-[10px] text-[#57534E] space-y-0.5" data-testid="reassign-work-project-list">
                        {reassignDryRun.projects.map((p) => <li key={p.id}>• {p.name} <span className="text-[#A8A29E]">({p.status})</span></li>)}
                      </ul>
                    )}
                    {reassignDryRun.tasks.length > 0 && (
                      <ul className="text-[10px] text-[#57534E] space-y-0.5" data-testid="reassign-work-task-list">
                        {reassignDryRun.tasks.map((t) => <li key={t.id}>• {t.name} <span className="text-[#A8A29E]">({t.status})</span></li>)}
                      </ul>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                  <button data-testid="reassign-work-cancel-btn" onClick={closeReassign} className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817]">Cancel</button>
                  {!reassignDryRun ? (
                    <button data-testid="reassign-work-dry-run-btn" disabled={reassignBusy || !reassignTargetId} onClick={runReassignDryRun} className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs font-medium text-[#57534E] hover:border-[#FFC817] disabled:opacity-50">
                      {reassignBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Check what would move
                    </button>
                  ) : (
                    <button data-testid="reassign-work-confirm-btn" disabled={reassignBusy || (reassignDryRun.counts.projects + reassignDryRun.counts.tasks === 0)} onClick={applyReassign} className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:bg-[#FFC817]/90 disabled:opacity-50">
                      {reassignBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Confirm move
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <EmailConfirmModal
        open={emailTestGuard.open}
        preview={emailTestGuard.preview}
        busy={emailTestGuard.busy}
        onConfirm={emailTestGuard.confirm}
        onCancel={emailTestGuard.cancel}
      />
      <EmailConfirmModal
        open={approvalGuard.open}
        preview={approvalGuard.preview}
        busy={approvalGuard.busy}
        onConfirm={approvalGuard.confirm}
        onCancel={approvalGuard.cancel}
      />
      <EmailConfirmModal
        open={staffEmailGuard.open}
        preview={staffEmailGuard.preview}
        busy={staffEmailGuard.busy}
        onConfirm={staffEmailGuard.confirm}
        onCancel={staffEmailGuard.cancel}
      />
    </div>
  );
}
